<template>
    <div>
        <v-row class="mb-0">
            <v-col class="pb-0">
                <h1 class="text-h6 primary--text text-capitalize">
                    {{$t('routes.visit-frequency-report')}}
                </h1>
            </v-col>
            
        </v-row>
        <!-- filter -->
        <v-form
        	:disabled="loading"
        	@submit.prevent="needToRestShownItems = true; tempVisits = []; filter();"
        >
            <v-row align="center" class="my-0">
                <!-- employee name -->
                <v-col md="2" sm="3" cols="12" class="py-1 pe-1 d-flex">
                    <v-autocomplete
                        v-model="employeeId"
                        :label="$t('inputs.employees')"
                        :items="employees.filter(c => c.employeeType === userType.medicalRep)"
                        item-text="name"
                        item-value="id"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>

                <!-- entity name -->
                <v-col md="2" sm="3" cols="12" class="py-1 pe-1 d-flex">
                    <v-text-field
                        v-model="entityName"
                        :label="$t('inputs.doctor')"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>
                
                <!-- city region filter -->
                <v-col lg="3" md="4" sm="6" cols="12" class="pa-1 ">
                    <v-row class="pa-3">
                        <!-- city name -->
                        <v-col cols="5" class="pa-0 pe-1 d-flex" >
                            <v-autocomplete
                                v-model="cityId"
                                :items="cities"
                                item-text="name"
                                item-value="id"
                                :return-object="false"
                                :label="$t('inputs.city')"
                                dense
                                hide-details
                                append-icon
                                outlined
                                prepend-icon="mdi-map-marker-multiple"
                            ></v-autocomplete>
                        </v-col>
            
                        <!-- region -->
                        <v-col cols="7" class="pa-0">
                            <v-autocomplete
                                v-model="regionIds"
                                :items="regionsAll.filter(c => !cityId || c.cityId === cityId)"
                                item-text="name"
                                item-value="id"
                                :return-object="false"
                                :label="$t('inputs.region')"
                                dense
                                multiple
                                hide-details
                                clearable
                                @click:clear="cityId = null"
                                outlined
                                :disabled="cityId === null"
                            >
                                <template v-slot:selection="{ item, index }">
                                    <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                                    <span
                                        v-if="index === 1"
                                        class="grey--text text-caption"
                                    >
                                        (+{{ regionIds.length - 1 }})
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-col>

                <!-- month -->
                <v-col md="2" sm="3" cols="12" class="pa-1">
                    <bee-date-input
                        v-model="month"
                        :label="$t('inputs.date')"
                        dense
                        hide-details
                        changeable-with-arrows
                        input-format-order="year-month-day"
                        input-type="month"
                        append-icon="mdi-menu-left"
                        prepend-icon="mdi-menu-right"
                        @click:append="month = moment(month, 'YYYY-MM').add(1, 'months').format('YYYY-MM')"
                        @click:prepend="month = moment(month, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')"
                    ></bee-date-input>
                </v-col>

                <!-- specialty -->
                <v-col md="2" sm="3" cols="12" class="pa-1">
                    <v-autocomplete
                        v-model="specialtyId"
                        :items="specialties"
                        item-text="name"
                        item-value="id"
                        :label="$t('inputs.specialty')"
                        dense
                        clearable
                        hide-details
                        outlined
                    ></v-autocomplete>
                </v-col>

                <v-spacer/>

                <v-col cols="auto">
                    <v-btn
                    	type="submit"
                    	color="primary"
                    	small
                        :disabled="loading"
                        :loading="loading"
                    >
                        {{$t('search')}}
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>

        <!-- table -->
        <bee-handy-table
            ref="table"
            :headers="headers"
            :items="[]"
            :loading="loading"
            with-columns-divider
            :items-per-page="20"
            pagination-on-scroll
            pagination-on-scroll-server-items
            :server-items-length="serverItemsLength"
            :options-scroll.sync="scrollOptions"
            :data-table-props="{ disableSort: true }"
            fixed-header
            :mobile-breakpoint="0"
            dense
            zebra
            :height="$vuetify.breakpoint.height - 140"
            @pagination-on-scroll:reach-last-row="filter"
        >
            <template
                v-for="header in headers.slice(1)"
            	v-slot:[`item.${header.name}`]="{ header, item, value }"
            >
                <div
                    :key="header.name"
                    :class="[
                        {
                            'success': value === visitStates.done,
                            'red': value === visitStates.missed,
                            'orange': value === visitStates.outOfPlan,
                        },
                        'state-cell'
                    ]"
                >
                    {{item.daysCount ? item.daysCount[header.value] : null}}
                </div>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import { entityType, userType } from '@/helpers/enums'

export default {
    name: 'VisitFrequencyReport',

    data () {
        return {
            userType,
            loading: false,
            
            // filter data
            entityName: null,
            cityId: null,
            regionIds: [],
            month: moment().format('YYYY-MM'),
            tableMonth: moment().format('YYYY-MM'),
            specialtyId: null,
            employeeId: null,

            // pagination
            scrollOptions: {},
            serverItemsLength: 0,
            needToRestShownItems: false,
            
            // data
            tempVisits: [],
            visitStates: {
                done: 1,
                outOfPlan: 2,
                missed: 3,
            }
        }
    },

    computed: {
        ...mapState({
            cities: state => state.cities.cities,
            regionsAll: state => state.regions.regionsAll,
            employees: state => state.medicalreps.medicalreps,
            allEntities: state => state.entities.allEntities,
            specialties: state => state.specialties.specialties,
        }),

        ...mapGetters({
            getEmployeeById: 'medicalreps/getMedicalRepById'
        }),
        
        headers () {
            const days = [];

            for (let i = 1; i <= moment(this.tableMonth, 'YYYY-MM').daysInMonth(); i++) {
                days.push({
                    text: moment(this.tableMonth + `-${i.toString().padStart(2, '0')}`).format('ddd DD').replace(/ /g, '\n'),
                    name: 'day' + i,
                    value: 'day' + i,
                    class: 'text-pre-wrap',
                    columnClass: 'px-0',
                    align: 'center',
                    width: 30
                })
            }
            return [
                {
                    text: this.$t('headers.doctor'),
                    name: 'entityName',
                    value: 'entityName',
                    width: 130
                },
                ...days
            ]
        }
    },

    methods: {
        filter(newScrollOptions) {
            this.tableMonth = this.month;
            let page = 1;
            const { itemsPerPage } = this.scrollOptions;

            if (newScrollOptions) {
                page = newScrollOptions.page;
            }

            this.loading = true;
            Promise.all([
                // this.$store.dispatch('entities/fetchAllBrief', ), 
                this.$store.dispatch('entities/fetchByFilter', {
                    // type: entityType.doctor,
                    name: this.entityName,
                    specialtyId: this.specialtyId,
                    regionIds: this.regionIds,
                    page: page,
                    perPage: itemsPerPage || 20,
                    OrderByName: true,
                    nonDeleted: true,
                    workloadId: undefined
                }),
                !this.tempVisits.length
                    ? this.$store.dispatch('visits/fetchVisitsWithoutPagination', {
                        fromDate: moment(this.month, 'YYYY-MM').startOf('months').format('YYYY-MM-DD'),
                        toDate: moment(this.month, 'YYYY-MM').endOf('months').format('YYYY-MM-DD'),
                        employeeId: this.employeeId,
                        regionIds: this.regionIds,
                        specialtyId: this.specialtyId,
                    })
                    : null
            ]).then((data) => {
                if (this.needToRestShownItems) {
                    this.$refs.table.resetShownRows();
                    this.needToRestShownItems = false;
                }

                const items = [];

                const entities = data[0].data;
                const totalItems = data[0].total;

                if (!this.tempVisits.length) this.tempVisits = data[1];
                const visits = this.tempVisits;
                
                // building table items
                entities.forEach(entity => {
                    const entityVisits = visits.filter(c => c.entityId === entity.id);
                    if (entityVisits.length) {
                        const obj = {};
                        const daysKeysObj = {};
                        
                        entityVisits.forEach(visit => {
                            const day = 'day' + moment(visit.createdAt).format('D');

                            // get visits count for each day 
                            if (day in daysKeysObj) {
                                if (!obj.daysCount) obj.daysCount = {};
                                obj.daysCount[day] = (obj.daysCount[day] || 1) + 1;
                            }

                            obj.entityName = visit.entity.name;
                            daysKeysObj[day] = this.visitStates.done;
                        })
                        items.push({ ...obj, ...daysKeysObj });
                    } else {
                        items.push({ entityName: entity.name })
                    }
                });

                this.serverItemsLength = totalItems;
                this.$refs.table.addScrollItems(items);
            }).finally(() => {
                this.loading = false;
            })
        },

        moment
    },

    created() {
        this.loading = true;

        Promise.all([
            !this.regionsAll.length
                ? this.$store.dispatch('cities/fetchCityMine').then(cities => {
                    this.$store.dispatch('regions/fetchAllRegions', { cities })
                }) : null,
            !this.specialties.length ? this.$store.dispatch('specialties/fetchAll') : null,
            !this.employees.length ? this.$store.dispatch('medicalreps/fetchAll', true) : null,
            this.filter()
        ]).finally(() => {
            if (this.allEntities.length) {
                this.loading = false;
            };
        })
    },

    metaInfo: {
        title: 'Visit frequency report',
        titleTemplate: '%s | Octopharma'
    }
}
</script>

<style scoped>
.state-cell {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
}
</style>
